/**
 * Control Room · Byråskanningen (sak 238, trinn 6; migrering 0482).
 *
 * `GET/PUT /v1/platform/scan/schedule`, `POST /v1/platform/scan/run`, `GET /v1/platform/scan/runs`.
 * Bare for plattformadmin; alle andre får 404.
 *
 * BARE TALL. En kjøring sier per organisasjon hvor mange byråer som ble lest, hvor mange artister som
 * kom inn, gikk ut eller byttet, og hvor mange funn som står under «Må sjekkes» — aldri hvilke. Navnene
 * står i organisasjonens egen fane Overganger, og Control Room gir aldri innsyn i kundens innhold.
 *
 * Uten `TAVILY_API_KEY` (og skanningens databaseadresse) er skanningen ikke satt opp: `configured` er
 * false, `missing` sier hva som mangler, og «Kjør nå» svarer `VALIDATION_FAILED` med detaljkoden
 * `scan_not_configured`. En kjøring som allerede går gir `scan_running`.
 */
import { z } from 'zod';

import { cursorQuerySchema, page } from '../http/pagination.js';
import { urlInputSchema } from '../url.js';

/** ISO-ukedag: 1 = mandag … 7 = søndag. */
export const SCAN_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/** Klokkeslettet i Europe/Oslo, «HH:MM». */
export const SCAN_TIME_PATTERN = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

export const SCAN_TIME_ZONE = 'Europe/Oslo';

/** Det som mangler for at skanningen skal kunne kjøres, i den rekkefølgen siden nevner dem. */
export const SCAN_MISSING = ['tavilyApiKey', 'scanDatabase'] as const;
export type ScanMissing = (typeof SCAN_MISSING)[number];

export const SCAN_RUN_TRIGGERS = ['schedule', 'manual'] as const;
export type ScanRunTrigger = (typeof SCAN_RUN_TRIGGERS)[number];

export const SCAN_RUN_STATUSES = ['running', 'succeeded', 'failed', 'interrupted'] as const;
export type ScanRunStatus = (typeof SCAN_RUN_STATUSES)[number];

/** Detaljkodene «Kjør nå» kan svare med. */
export const SCAN_REFUSAL_CODES = ['scan_not_configured', 'scan_running'] as const;
export type ScanRefusalCode = (typeof SCAN_REFUSAL_CODES)[number];

export const scanScheduleResponseSchema = z
  .object({
    weekday: z.number().int().min(1).max(7),
    time: z.string().regex(SCAN_TIME_PATTERN),
    enabled: z.boolean(),
    timeZone: z.literal(SCAN_TIME_ZONE),
    /** Neste gang tidsplanen slår til, eller null når den er av. */
    nextRunAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
    /** Henteren har nøkkel og database. Uten dem står siden «Ikke satt opp». */
    configured: z.boolean(),
    missing: z.array(z.enum(SCAN_MISSING)),
    /** En kjøring går nå. */
    running: z.boolean(),
  })
  .meta({ id: 'ScanScheduleResponse' });

export const updateScanScheduleRequestSchema = z
  .object({
    weekday: z.number().int().min(1).max(7),
    time: z.string().regex(SCAN_TIME_PATTERN, { message: 'Use HH:MM (00:00–23:59).' }),
    enabled: z.boolean(),
  })
  .meta({ id: 'UpdateScanScheduleRequest' });

/** Én organisasjon i en kjøring. Bare tall. */
export const scanRunOrganizationSchema = z
  .object({
    organizationId: z.uuid(),
    /** Organisasjonens navn (generell info), eller null om den er slettet siden. */
    organizationName: z.string().nullable(),
    ok: z.boolean(),
    agencies: z.number().int().min(0),
    unreadable: z.number().int().min(0),
    in: z.number().int().min(0),
    out: z.number().int().min(0),
    switched: z.number().int().min(0),
    agentChanges: z.number().int().min(0),
    newArtists: z.number().int().min(0),
    newAgencies: z.number().int().min(0),
    review: z.number().int().min(0),
  })
  .meta({ id: 'ScanRunOrganization' });

export const scanRunSchema = z
  .object({
    id: z.uuid(),
    trigger: z.enum(SCAN_RUN_TRIGGERS),
    status: z.enum(SCAN_RUN_STATUSES),
    startedAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
    /** E-postadressen til plattformadminen som trykket «Kjør nå»; null for tidsplanen. */
    startedByEmail: z.string().nullable(),
    organizationCount: z.number().int().min(0),
    /** Antall «Må sjekkes» til sammen. */
    reviewCount: z.number().int().min(0),
    errorCount: z.number().int().min(0),
    organizations: z.array(scanRunOrganizationSchema),
  })
  .meta({ id: 'ScanRun' });

export const SCAN_RUN_SORT_KEYS = ['recent'] as const;

export const listScanRunsQuerySchema = cursorQuerySchema(SCAN_RUN_SORT_KEYS)
  .extend({
    limit: z.coerce.number().int().min(1).max(100).default(20),
    direction: z.literal('desc').default('desc'),
  })
  .meta({ id: 'ListScanRunsQuery' });

export const scanRunListResponseSchema = page(scanRunSchema, 'ScanRunListResponse');

export type ScanScheduleResponse = z.infer<typeof scanScheduleResponseSchema>;
export type UpdateScanScheduleRequest = z.infer<typeof updateScanScheduleRequestSchema>;
export type ScanRunOrganization = z.infer<typeof scanRunOrganizationSchema>;
export type ScanRun = z.infer<typeof scanRunSchema>;
export type ListScanRunsQuery = z.infer<typeof listScanRunsQuerySchema>;
export type ScanRunListResponse = z.infer<typeof scanRunListResponseSchema>;

// ------------------------------------------------------------------------------ byråene (sak 243)

/**
 * `GET /v1/platform/scan/agencies` og `PATCH /v1/platform/scan/agencies/:id` (0840).
 *
 * Byråene skanningen leser: de i organisasjoner med et bookingregister (Greenroom-grunnlinje), som
 * ikke selv er en organisasjon i Tvitvi. Navn, organisasjon, nettadresse og artistside — ingen
 * artister og ingen agenter. Artistsiden er det eneste som kan rettes her: satt, leser skanningen den
 * siden; tom, finner skanningen lista selv fra nettadressen, som før.
 */
export const scanAgencySchema = z
  .object({
    id: z.uuid(),
    organizationId: z.uuid(),
    organizationName: z.string(),
    name: z.string(),
    websiteUrl: z.string().nullable(),
    artistPageUrl: z.string().nullable(),
    origin: z.enum(['own', 'register']),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'ScanAgency' });

export const SCAN_AGENCY_SORT_KEYS = ['name'] as const;

export const listScanAgenciesQuerySchema = cursorQuerySchema(SCAN_AGENCY_SORT_KEYS)
  .extend({
    limit: z.coerce.number().int().min(1).max(100).default(50),
    direction: z.literal('asc').default('asc'),
    /** Søk i navnet og nettadressen. */
    q: z.string().trim().max(100).optional(),
  })
  .meta({ id: 'ListScanAgenciesQuery' });

export const scanAgencyListResponseSchema = page(scanAgencySchema, 'ScanAgencyListResponse');

export const updateScanAgencyRequestSchema = z
  .object({
    /** Null tar artistsiden bort. */
    artistPageUrl: urlInputSchema(2048).nullable(),
  })
  .meta({ id: 'UpdateScanAgencyRequest' });

export type ScanAgency = z.infer<typeof scanAgencySchema>;
export type ListScanAgenciesQuery = z.infer<typeof listScanAgenciesQuerySchema>;
export type ScanAgencyListResponse = z.infer<typeof scanAgencyListResponseSchema>;
export type UpdateScanAgencyRequest = z.infer<typeof updateScanAgencyRequestSchema>;
